import { useQuery } from '@tanstack/react-query';
import { stocksAPI, aiRecommendationsAPI, valuationsAPI } from '../services/api';

// Hook for fetching stocks with filters
export const useStocks = ({ page, rowsPerPage, searchTerm, exchangeFilter, valuationFilter, enabled = true }) => {
  return useQuery({
    queryKey: ['stocks', page, rowsPerPage, searchTerm, exchangeFilter, valuationFilter],
    queryFn: async () => {
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        is_active: true
      };
      if (exchangeFilter) params.exchange = exchangeFilter;
      if (searchTerm) params.search = searchTerm;
      if (valuationFilter) params.valuation_type = valuationFilter;
      
      const response = await stocksAPI.getStocks(params);
      return {
        stocks: response.data.stocks || [],
        total: response.data.total || 0
      };
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Hook for fetching AI recommendations (New authenticated endpoint)
export const useRecommendations = (enabled = true) => {
  return useQuery({
    queryKey: ['ai-recommendations'],
    queryFn: async () => {
      const response = await aiRecommendationsAPI.getAllRecommendations();
      // Transform the response to match the expected format
      return response.data.recommendations || {};
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Hook for fetching sectors
export const useSectors = (enabled = true) => {
  return useQuery({
    queryKey: ['sectors'],
    queryFn: async () => {
      const response = await stocksAPI.getSectorStats();
      return response.data.sectors || [];
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Hook for fetching valuation stats
export const useValuationStats = () => {
  return useQuery({
    queryKey: ['valuationStats'],
    queryFn: async () => {
      const response = await stocksAPI.getValuationStats();
      return {
        total_stocks: response.data.total_stocks,
        with_valuation: response.data.with_valuation,
        undervalued: response.data.undervalued,
        neutral: response.data.neutral,
        overvalued: response.data.overvalued,
        coverage_pct: response.data.coverage_pct
      };
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });
};

// Hook for fetching AI sector insights
export const useAIInsights = (topN = 3) => {
  return useQuery({
    queryKey: ['ai-sector-insights', topN],
    queryFn: async () => {
      const response = await valuationsAPI.getTopSectorsAIInsights(topN);
      return response.data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - AI insights don't change often
    cacheTime: 30 * 60 * 1000, // 30 minutes
    retry: 1,
  });
};

